/**
 * カスタム値管理クラス
 * スタイル、モチーフ、サブカテゴリのカスタム値を管理
 */

import type { CustomValue, BaseManager } from './types'

export class CustomValuesManager implements BaseManager {
  constructor(public db: IDBDatabase | null) {}

  async addCustomValue(customValue: Omit<CustomValue, 'id' | 'createdAt' | 'usageCount'>): Promise<CustomValue> {
    if (!this.db) throw new Error('Database not initialized')
    
    const newValue: CustomValue = {
      ...customValue,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      usageCount: 0,
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['customValues'], 'readwrite')
      const store = transaction.objectStore('customValues')
      const request = store.add(newValue)

      request.onsuccess = () => resolve(newValue)
      request.onerror = () => reject(new Error('Failed to add custom value'))
    })
  }

  async getCustomValues(type?: CustomValue['type'], nameType?: string): Promise<CustomValue[]> {
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['customValues'], 'readonly')
      const store = transaction.objectStore('customValues')
      const request = store.getAll()

      request.onsuccess = () => {
        let values = request.result as CustomValue[]
        
        if (type) {
          values = values.filter(v => v.type === type)
        }
        
        if (nameType) {
          values = values.filter(v => v.nameType === nameType)
        }

        // 使用頻度でソート
        values.sort((a, b) => b.usageCount - a.usageCount)
        
        resolve(values)
      }
      request.onerror = () => reject(new Error('Failed to get custom values'))
    })
  }

  async updateCustomValueUsage(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['customValues'], 'readwrite')
      const store = transaction.objectStore('customValues')
      const getRequest = store.get(id)

      getRequest.onsuccess = () => {
        const value = getRequest.result as CustomValue
        if (value) {
          value.usageCount++
          value.lastUsedAt = new Date()
          const putRequest = store.put(value)
          putRequest.onsuccess = () => resolve()
          putRequest.onerror = () => reject(new Error('Failed to update usage'))
        } else {
          resolve()
        }
      }
      getRequest.onerror = () => reject(new Error('Failed to get custom value'))
    })
  }

  async deleteCustomValue(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['customValues'], 'readwrite')
      const store = transaction.objectStore('customValues')
      const request = store.delete(id)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to delete custom value'))
    })
  }
}